/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react'
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import moment from 'moment'
import { IDealerGroups, IUpdateDealerGroupStatusRequest } from '../../types'
import { EditButton } from '../Buttons'
import { capitalize } from 'lodash'

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const aValue = typeof a[orderBy] === 'string' ? String(a[orderBy]).toLowerCase() : a[orderBy]
  const bValue = typeof b[orderBy] === 'string' ? String(b[orderBy]).toLowerCase() : b[orderBy]

  if (bValue < aValue) {
    return -1
  }
  if (bValue > aValue) {
    return 1
  }
  return 0
}

type Order = 'asc' | 'desc'

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

interface HeadCell {
  id: keyof IDealerGroups
  label: string
}

const headCells: readonly HeadCell[] = [
  {
    id: 'application',
    label: 'Application',
  },
  {
    id: 'dealerGroup',
    label: 'Dealer Group',
  },
  {
    id: 'dealerGroupNumber',
    label: 'Dealer Group Number',
  },
  {
    id: 'dealerId',
    label: 'Dealer ID',
  },
  {
    id: 'dealerLogoPath',
    label: 'Dealer Logo',
  },
  {
    id: 'createdOn',
    label: 'Created',
  },
  {
    id: 'createdBy',
    label: 'Created By',
  },
  {
    id: 'updatedOn',
    label: 'Updated',
  },
  {
    id: 'updatedBy',
    label: 'Updated By',
  },
]

interface DealerGroupTableHeadProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IDealerGroups) => void
  order: Order
  orderBy: string
}

function DealerGroupTableHead(props: DealerGroupTableHeadProps) {
  const { order, orderBy, onRequestSort } = props
  const createSortHandler =
    (property: keyof IDealerGroups) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: '#1976d2' }}>
        <TableCell padding='checkbox'></TableCell>
        <TableCell sx={{ border: 'none', color: 'white' }}>Status</TableCell>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{ color: 'white' }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component='span' sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

interface Props {
  rows: IDealerGroups[]
  disableApplication: (data: IUpdateDealerGroupStatusRequest) => void
  editApplication: (data: IDealerGroups) => void
  searchKey?: string
}

export const DealerGroupTable = ({
  rows,
  searchKey = '',
  disableApplication,
  editApplication,
}: Props) => {
  const [order, setOrder] = React.useState<Order>('asc')
  const [orderBy, setOrderBy] = React.useState<keyof IDealerGroups>('dealerGroup')
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(5)

  // Filter rows based on searchKey across all fields
  const filteredRows = rows.filter((dealerGroup) =>
    headCells.some((headCell) =>
      String(dealerGroup[headCell.id])?.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase()),
    ),
  )

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IDealerGroups) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby='applicationTable'>
            <DealerGroupTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {/* @ts-ignore */}
              {stableSort(filteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <TableRow
                      hover
                      role='checkbox'
                      tabIndex={-1}
                      key={`${row.dealerGroupNumberToLogoId || row.dealerId}_${index}`}
                    >
                      <TableCell>
                        <EditButton
                          //@ts-ignore
                          edit={() => editApplication(row)}
                          disable={() => {
                            disableApplication({
                              dealerGroupId: row.dealerGroupId ?? (row.dealerGroupId as number),
                              isActiveFlg: row.isActiveFlg ? 0 : 1,
                              application: 'Intranet',
                            })
                          }}
                          isActive={row.isActiveFlg}
                        />
                      </TableCell>
                      <TableCell>
                        {row?.isActiveFlg ? (
                          <Chip label='Active' color='primary' size='small' />
                        ) : (
                          <Chip label='Deactive' color='error' size='small' />
                        )}
                      </TableCell>
                      <TableCell>{capitalize(row?.application as string)}</TableCell>
                      <TableCell>
                        {capitalize(row?.dealerGroupName as string) || (row?.dealerGroup as string)}
                      </TableCell>
                      <TableCell>{row?.dealerGroupNumber as string}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {Array.isArray(row.dealerId) && row.dealerId.length > 0
                          ? (row.dealerId as any[])?.slice(0, 4)?.join('; ') +
                            ((row.dealerId as any[])?.length > 4 ? ' ...' : '')
                          : row.dealerId || '-'}
                      </TableCell>

                      <TableCell>
                        {row?.dealerLogoPath ? (
                          <img
                            src={row?.dealerLogoPath as string}
                            alt={
                              row?.dealerLogoPath ? (row?.dealerLogoPath as string) : 'Logo image'
                            }
                            width='72'
                          />
                        ) : (
                          <React.Fragment>No image</React.Fragment>
                        )}
                      </TableCell>
                      <TableCell>{moment(row?.createdOn).format('L')}</TableCell>
                      <TableCell>{row?.createdBy ? row?.createdBy : ''}</TableCell>
                      <TableCell>
                        {row?.updatedOn ? moment(row.updatedOn).format('L') : ''}
                      </TableCell>
                      <TableCell>{row?.updatedBy ? row?.updatedBy : ''}</TableCell>
                    </TableRow>
                  )
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={11} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={filteredRows.length} // Use filteredRows length for pagination
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  )
}
